import React, { useCallback, useContext, useEffect } from 'react'
import { StandaloneFlightMenu } from 'components/Flights/FlightsSearch/SearchWidget/SearchWidget'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { addDays } from 'lib/dateUtils'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import Breakpoint from 'components/Common/Breakpoint'
import DualDateSelectToogle from './DualDateSelectToogle'
import DateSelectToogle from './DateSelectToggle'
import { FlightsFareTypes } from 'constants/flight'
import FlightSearchMenuDesktop from '../FlightSearchMenu/FlightSearchMenuDesktop'
import Spacing from 'components/Common/Spacing'
import FlightsDatePicker from '../FlightsDatePicker/FlightsDatePicker'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightSearchMenu from '../FlightSearchMenu/FlightSearchMenu'

interface Props {
  className?: string;
  useOnlyMobile?: boolean;
}

const earliestBookingDate = addDays(new Date(), 2)
const finalBookingDate = addDays(new Date(), 330)

function DateSelectInput({ className, useOnlyMobile }: Props) {
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    activeMenu,
    fareType,
    showTravelDatesError,
    flightDeal,
  } = state

  const setActiveMenu = useCallback((activeMenu: StandaloneFlightMenu) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu })
  }, [dispatch])

  const cancelMenu = useCallback(() => {
    setActiveMenu(null)
  }, [setActiveMenu])

  const openMenu = useCallback(() => {
    setActiveMenu(StandaloneFlightMenu.date)
  }, [setActiveMenu])

  const onDatesChanged = useCallback((dates: { startDate: moment.Moment; endDate: moment.Moment; }) => {
    const { startDate, endDate } = dates

    if (fareType.value === FlightsFareTypes.RETURN) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: startDate })
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: endDate })
      startDate && endDate && cancelMenu()
    } else {
      // one-way fare
      const departDate = endDate ?? startDate
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: departDate })
      departDate && cancelMenu()
    }
  }, [cancelMenu, dispatch, fareType.value])

  const getDatePicker = (type: 'single' | 'dual') => {
    return <div>
      <FlightsDatePicker
        onDatesChange={onDatesChanged}
        startDate={checkinDate}
        endDate={checkoutDate}
        minDate={earliestBookingDate}
        maxDate={finalBookingDate}
        departureAirport={departureAirport}
        arrivalAirport={arrivalAirport}
        type={type}
        fareType={fareType}
      />
      {flightDeal && <Spacing mt={24}>
        <FlightDealDuration flightDeal={flightDeal} />
      </Spacing>}
    </div>
  }

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: null })
    }
  }, [dispatch, fareType.value])

  return (
    <div className={className}>
      {fareType.value === FlightsFareTypes.ONE_WAY && (
        <DateSelectToogle
          checkinDate={checkinDate}
          openMenu={openMenu}
          showTravelDatesError={showTravelDatesError}
        />
      )}
      {fareType.value === FlightsFareTypes.RETURN && (
        <DualDateSelectToogle
          checkinDate={checkinDate}
          checkoutDate={checkoutDate}
          openMenu={openMenu}
          showTravelDatesError={showTravelDatesError}
        />
      )}

      {!useOnlyMobile && (
        <Breakpoint min="md">
          <FlightSearchMenuDesktop
            open={activeMenu === StandaloneFlightMenu.date}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            onCancel={cancelMenu}
            onApply={cancelMenu}
          >
            <Spacing pt={24} pb={24} pl={32} pr={32}>
              {getDatePicker('dual')}
            </Spacing>
          </FlightSearchMenuDesktop>
        </Breakpoint>
      )}

      <Breakpoint max={useOnlyMobile ? undefined : 'sm'}>
        <FlightSearchMenu
          open={activeMenu === StandaloneFlightMenu.date}
          title="When do you want to go?"
          checkinDate={checkinDate}
          checkoutDate={checkoutDate}
          onApply={cancelMenu}
          onCancel={cancelMenu}
        >
          {getDatePicker('single')}
        </FlightSearchMenu>
      </Breakpoint>
    </div>
  )
}

export default DateSelectInput
